import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Globe, Phone, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "@/components/ClientPicker";
import { changeTicketStatus } from "@/lib/tickets.functions";

export const Route = createFileRoute("/tools/service-tickets")({
  head: () => ({ meta: [{ title: "Support Tickets" }, { name: "robots", content: "noindex" }] }),
  component: TicketsPage,
});

type Ticket = {
  id: string;
  ticket_number: number;
  client_id?: string | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  client_address: string | null;
  issue: string;
  resolution: string | null;
  status: string;
  priority: string;
  assigned_to: string | null;
  scheduled_at: string | null;
  resolved_at: string | null;
  created_at: string;
  source?: string;
  category?: string | null;
  system_brand?: string | null;
  preferred_contact?: string | null;
};
type Note = { id: string; body: string; author: string | null; kind: string; created_at: string };

const STATUSES = ["open", "scheduled", "in_progress", "waiting_parts", "resolved", "closed"] as const;
const lbl = (s: string) => s.replace(/_/g, " ");

const empty = (): Partial<Ticket> => ({ client_name: "", issue: "", status: "open", priority: "normal" });

const priColor: Record<string, string> = {
  urgent: "bg-red-500/20 text-red-300 border-red-500/40",
  high: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  normal: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  low: "bg-gray-500/20 text-gray-300 border-gray-500/40",
};
const statColor: Record<string, string> = {
  open: "bg-blue-500/20 text-blue-300",
  scheduled: "bg-yellow-500/20 text-yellow-300",
  in_progress: "bg-purple-500/20 text-purple-300",
  waiting_parts: "bg-orange-500/20 text-orange-300",
  resolved: "bg-green-500/20 text-green-300",
  closed: "bg-gray-500/20 text-gray-300",
};

const ageDays = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

function TicketsPage() {
  const [list, setList] = useState<Ticket[]>([]);
  const [current, setCurrent] = useState<Partial<Ticket> | null>(null);
  const [origStatus, setOrigStatus] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState("");
  const [filter, setFilter] = useState<string>("active");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await supabase.from("service_tickets").select("*").order("created_at", { ascending: false });
    setList((data as Ticket[]) || []);
  };
  useEffect(() => { load(); }, []);

  const loadNotes = async (id: string) => {
    const { data } = await supabase.from("service_ticket_notes").select("*").eq("ticket_id", id).order("created_at", { ascending: false });
    setNotes((data as Note[]) || []);
  };

  const open = (t: Partial<Ticket>) => {
    setCurrent(t);
    setOrigStatus(t.id ? t.status ?? null : null);
    setNotes([]);
    setNewNote("");
    if (t.id) loadNotes(t.id);
  };

  const save = async () => {
    if (!current) return;
    if (!current.client_name?.trim() || !current.issue?.trim()) return toast.error("Client name and issue are required");
    const { id, ticket_number, created_at, status, ...fields } = current as Ticket;
    if (id) {
      const { error } = await supabase.from("service_tickets").update(fields).eq("id", id);
      if (error) return toast.error(error.message);
      if (status && status !== origStatus) {
        try {
          const r = await changeTicketStatus({ data: { id, status: status as (typeof STATUSES)[number] } });
          toast.success(r.emailed ? "Saved — status change emailed to info@apexav.com" : "Saved (status email not sent)");
        } catch (e) {
          return toast.error(e instanceof Error ? e.message : "Status change failed");
        }
      } else toast.success("Saved");
    } else {
      const { error } = await supabase.from("service_tickets").insert({ ...fields, status: status || "open", source: "tools" } as never);
      if (error) return toast.error(error.message);
      toast.success("Ticket created");
    }
    setCurrent(null);
    load();
  };

  const addNote = async () => {
    if (!current?.id || !newNote.trim()) return;
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("service_ticket_notes").insert({ ticket_id: current.id, body: newNote.trim(), author: u.user?.email ?? null });
    if (error) return toast.error(error.message);
    setNewNote("");
    loadNotes(current.id);
  };

  const delNote = async (id: string) => {
    await supabase.from("service_ticket_notes").delete().eq("id", id);
    if (current?.id) loadNotes(current.id);
  };

  const del = async (id: string) => {
    if (!confirm("Delete ticket?")) return;
    await supabase.from("service_tickets").delete().eq("id", id);
    load();
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return list.filter((t) => {
      if (filter === "active" && ["resolved", "closed"].includes(t.status)) return false;
      if (filter === "website" && t.source !== "website") return false;
      if (!["all", "active", "website"].includes(filter) && t.status !== filter) return false;
      if (!term) return true;
      return [t.client_name, t.issue, t.client_email, t.client_phone, t.client_address, String(t.ticket_number), t.category, t.system_brand]
        .some((v) => v?.toLowerCase().includes(term));
    });
  }, [list, filter, q]);

  const stats = useMemo(() => {
    const active = list.filter((t) => !["resolved", "closed"].includes(t.status));
    const resolved = list.filter((t) => t.resolved_at);
    const avg = resolved.length
      ? resolved.reduce((s, t) => s + (new Date(t.resolved_at!).getTime() - new Date(t.created_at).getTime()), 0) / resolved.length / 86400000
      : null;
    return {
      active: active.length,
      urgent: active.filter((t) => t.priority === "urgent" || t.priority === "high").length,
      stale: active.filter((t) => ageDays(t.created_at) >= 3 && t.status === "open").length,
      avg,
    };
  }, [list]);

  if (current) {
    const sel = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
    return (
      <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <div className="mb-4 flex justify-between">
            <Button variant="ghost" size="sm" onClick={() => setCurrent(null)}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
            <Button size="sm" onClick={save}><Save className="mr-1 h-4 w-4" /> Save</Button>
          </div>
          {current.ticket_number && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="font-mono">#{current.ticket_number}</span>
              {current.source === "website" && <span className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs"><Globe className="h-3 w-3" /> Website request</span>}
              {current.created_at && <span>Opened {new Date(current.created_at).toLocaleString()}</span>}
            </div>
          )}
          <div className="space-y-3">
            <ClientPicker clientId={current.client_id} clientName={current.client_name} onPick={(f) => setCurrent({ ...current, client_id: f.client_id, client_name: f.client_name ?? "", client_email: f.client_email, client_phone: f.client_phone, client_address: f.client_address })} />
            <F label="Client name *"><Input value={current.client_name || ""} onChange={(e) => setCurrent({ ...current, client_name: e.target.value })} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Phone"><Input value={current.client_phone || ""} onChange={(e) => setCurrent({ ...current, client_phone: e.target.value })} /></F>
              <F label="Email"><Input value={current.client_email || ""} onChange={(e) => setCurrent({ ...current, client_email: e.target.value })} /></F>
            </div>
            {(current.client_phone || current.client_email) && (
              <div className="flex gap-2">
                {current.client_phone && <Button asChild size="sm" variant="outline"><a href={`tel:${current.client_phone}`}><Phone className="mr-1 h-4 w-4" /> Call</a></Button>}
                {current.client_email && <Button asChild size="sm" variant="outline"><a href={`mailto:${current.client_email}?subject=${encodeURIComponent(`Apex AV support ticket #${current.ticket_number ?? ""}`)}`}><Mail className="mr-1 h-4 w-4" /> Email</a></Button>}
                {current.preferred_contact && <span className="self-center text-xs text-muted-foreground">Prefers: {current.preferred_contact}</span>}
              </div>
            )}
            <F label="Address"><Input value={current.client_address || ""} onChange={(e) => setCurrent({ ...current, client_address: e.target.value })} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="System"><Input value={current.category || ""} onChange={(e) => setCurrent({ ...current, category: e.target.value })} /></F>
              <F label="Brand / device"><Input value={current.system_brand || ""} onChange={(e) => setCurrent({ ...current, system_brand: e.target.value })} /></F>
            </div>
            <F label="Issue *"><Textarea rows={4} value={current.issue || ""} onChange={(e) => setCurrent({ ...current, issue: e.target.value })} /></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Priority">
                <select className={sel} value={current.priority} onChange={(e) => setCurrent({ ...current, priority: e.target.value })}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </F>
              <F label="Status">
                <select className={sel} value={current.status} onChange={(e) => setCurrent({ ...current, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{lbl(s)}</option>)}
                </select>
              </F>
              <F label="Assigned to"><Input value={current.assigned_to || ""} onChange={(e) => setCurrent({ ...current, assigned_to: e.target.value })} /></F>
            </div>
            {current.id && current.status !== origStatus && <p className="text-xs text-yellow-400">Saving will email the status change to info@apexav.com.</p>}
            <F label="Scheduled for"><Input type="datetime-local" value={current.scheduled_at?.slice(0, 16) || ""} onChange={(e) => setCurrent({ ...current, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></F>
            <F label="Resolution"><Textarea rows={3} value={current.resolution || ""} onChange={(e) => setCurrent({ ...current, resolution: e.target.value })} /></F>
          </div>
        </div>

        <div>
          <h2 className="mb-3 font-display text-lg font-bold">Notes & history</h2>
          {current.id ? (
            <>
              <div className="mb-4 space-y-2">
                <Textarea rows={3} placeholder="Add a note (call log, parts needed, next steps…)" value={newNote} onChange={(e) => setNewNote(e.target.value)} />
                <Button size="sm" onClick={addNote} disabled={!newNote.trim()}><Send className="mr-1 h-4 w-4" /> Add note</Button>
              </div>
              <div className="space-y-2">
                {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
                {notes.map((n) => (
                  <div key={n.id} className={`rounded-md border p-3 text-sm ${n.kind === "status" ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                    <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                      <span>{n.author || "Staff"} · {new Date(n.created_at).toLocaleString()}</span>
                      {n.kind !== "status" && <button onClick={() => delNote(n.id)} aria-label="Delete note" className="hover:text-foreground"><Trash2 className="h-3 w-3" /></button>}
                    </div>
                    <div className="whitespace-pre-wrap capitalize-first">{n.body}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Save the ticket first to add notes.</p>
          )}
        </div>
      </div>
    );
  }

  const filters = ["active", "all", "website", ...STATUSES];
  const count = (s: string) =>
    s === "all" ? list.length
    : s === "active" ? list.filter((t) => !["resolved", "closed"].includes(t.status)).length
    : s === "website" ? list.filter((t) => t.source === "website").length
    : list.filter((t) => t.status === s).length;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Support Tickets</h1>
          <p className="mt-1 text-sm text-muted-foreground">Requests from apexav.com/support plus service calls you log here.</p>
        </div>
        <Button onClick={() => open(empty())}><Plus className="mr-1 h-4 w-4" /> New ticket</Button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Active" value={stats.active} />
        <Stat label="High / urgent" value={stats.urgent} />
        <Stat label="Open 3+ days" value={stats.stale} />
        <Stat label="Avg. days to resolve" value={stats.avg === null ? "—" : stats.avg.toFixed(1)} />
      </div>

      <Input className="mb-3 max-w-sm" placeholder="Search name, issue, phone, ticket #…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="mb-4 flex flex-wrap gap-2">
        {filters.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full border px-3 py-1 text-xs capitalize ${filter === s ? "bg-foreground text-background" : "border-border hover:bg-accent"}`}>
            {lbl(s)} ({count(s)})
          </button>
        ))}
      </div>
      <div className="divide-y divide-border rounded-lg border border-border">
        {filtered.length === 0 && <div className="p-12 text-center text-muted-foreground">No tickets.</div>}
        {filtered.map((t) => {
          const age = ageDays(t.created_at);
          return (
            <div key={t.id} className="flex items-center gap-3 p-4 hover:bg-accent">
              <button onClick={() => open(t)} className="flex-1 text-left">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">#{t.ticket_number}</span>
                  <span className="font-medium">{t.client_name}</span>
                  <span className={`rounded border px-2 py-0.5 text-[10px] uppercase ${priColor[t.priority] ?? ""}`}>{t.priority}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] uppercase ${statColor[t.status] ?? ""}`}>{lbl(t.status)}</span>
                  {t.source === "website" && <Globe className="h-3 w-3 text-muted-foreground" aria-label="Website request" />}
                  {t.category && <span className="text-xs text-muted-foreground">{t.category}</span>}
                </div>
                <div className="line-clamp-1 text-xs text-muted-foreground">{t.issue}</div>
                <div className="mt-1 flex gap-3 text-xs">
                  <span className={!["resolved", "closed"].includes(t.status) && age >= 3 ? "text-red-400" : "text-muted-foreground"}>{age === 0 ? "Today" : `${age}d old`}</span>
                  {t.assigned_to && <span className="text-muted-foreground">→ {t.assigned_to}</span>}
                  {t.scheduled_at && <span className="text-yellow-400">Scheduled: {new Date(t.scheduled_at).toLocaleString()}</span>}
                </div>
              </button>
              <Button variant="ghost" size="icon" onClick={() => del(t.id)} aria-label="Delete ticket"><Trash2 className="h-4 w-4" /></Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold">{value}</div>
    </div>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (<label className="block"><div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>{children}</label>);
}
